import { useEffect, useRef, useState } from 'react';
import { Input } from '../../ui';
import { useAdminApi } from '../../hooks/useAdminApi';

interface ApplicationDetail {
  application_id: number;
  status: string;
  applied_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  cover_letter: string | null;
  candidate_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  current_position: string | null;
  current_company: string | null;
  years_experience: number | null;
  education: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  career_id: number;
  career_title: string;
  department: string;
  location: string;
  employment_type: string;
}

const STATUS_COLORS: Record<string, string> = {
  applied: 'bg-blue-100 text-blue-800',
  reviewed: 'bg-yellow-100 text-yellow-800',
  interviewed: 'bg-indigo-100 text-indigo-800',
  offered: 'bg-emerald-100 text-emerald-800',
  hired: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const VALID_STATUSES = ['applied', 'reviewed', 'interviewed', 'offered', 'hired', 'rejected'];

export default function AdminApplications() {
  const [applications, setApplications] = useState<ApplicationDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const api = useAdminApi();

  const fetchApplications = () => {
    api.get('/api/admin/applications')
      .then((res) => setApplications(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const initRef = useRef(false);
  useEffect(() => { if (!initRef.current) { initRef.current = true; fetchApplications(); } }, [api]);

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await api.put(`/api/admin/applications/${id}/status`, { status });
      fetchApplications();
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const filtered = applications.filter((a) => {
    if (statusFilter && a.status !== statusFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return `${a.first_name} ${a.last_name}`.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.career_title.toLowerCase().includes(q) ||
      a.department.toLowerCase().includes(q);
  });

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading applications...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Applications ({applications.length})</h2>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border rounded-md px-2 py-1.5"
          >
            <option value="">All Statuses</option>
            {VALID_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Input
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Candidate</th>
              <th className="px-3 py-2 text-left font-medium">Position</th>
              <th className="px-3 py-2 text-left font-medium">Department</th>
              <th className="px-3 py-2 text-left font-medium">Experience</th>
              <th className="px-3 py-2 text-left font-medium">Applied</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <>
                <tr
                  key={a.application_id}
                  className="border-b hover:bg-muted/30 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === a.application_id ? null : a.application_id)}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{a.first_name} {a.last_name}</div>
                    <div className="text-xs text-muted-foreground">{a.email}</div>
                  </td>
                  <td className="px-3 py-2">{a.career_title}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.department}</td>
                  <td className="px-3 py-2">
                    {a.years_experience ? `${a.years_experience} yrs` : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                    {new Date(a.applied_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || ''}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={a.status}
                      onChange={(e) => handleStatusUpdate(a.application_id, e.target.value)}
                      className="text-xs border rounded px-1 py-0.5"
                    >
                      {VALID_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                {expandedId === a.application_id && (
                  <tr key={`${a.application_id}-detail`}>
                    <td colSpan={7} className="px-3 py-3 bg-muted/20">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="font-medium">Phone:</span> {a.phone || '—'}</div>
                        <div><span className="font-medium">Current Role:</span> {a.current_position || '—'}</div>
                        <div><span className="font-medium">Company:</span> {a.current_company || '—'}</div>
                        <div><span className="font-medium">Education:</span> {a.education || '—'}</div>
                        <div><span className="font-medium">Location:</span> {a.location}</div>
                        <div><span className="font-medium">Type:</span> {a.employment_type}</div>
                        {a.linkedin_url && (
                          <div>
                            <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">LinkedIn</a>
                          </div>
                        )}
                        {a.github_url && (
                          <div>
                            <a href={a.github_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">GitHub</a>
                          </div>
                        )}
                        {a.reviewed_by && (
                          <div><span className="font-medium">Reviewed by:</span> {a.reviewed_by}</div>
                        )}
                      </div>
                      {a.cover_letter && (
                        <div className="mt-3 border-t pt-2">
                          <span className="font-medium text-sm">Cover Letter:</span>
                          <p className="text-sm mt-1 whitespace-pre-wrap">{a.cover_letter}</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {filter || statusFilter ? 'No applications match your filters' : 'No applications yet'}
          </div>
        )}
      </div>
    </div>
  );
}
